// src/screens/admin/SuperAdminUserReportScreen.js
// 총관리자 유저·신고 관리 화면
// 28차: 유저관리 — reportStats(신고 당한 횟수 / 처리된 횟수) 표시
//        신고 처리(resolved) 시 피신고자 users.reportStats.processedCount +1
// 32차: 신고 처리(resolved) / 기각(dismissed) 후 피신고자 인앱 알림 발송
// 51차: 색/폰트 theme.js 단일화 — onGold·dangerSoft 토큰, 'serif'→FONTS.display
// 74차: 회원 목록에 users.isDemoAccount 배지 추가 — 영업용 데모 매장관리자 테스트 계정을 구분
// 75차: 신고 처리(resolved) 시 기간(1/3/7일) 지정 모집글 작성 제한 부여 + 유저관리 탭에서
//       수동 해제 기능 추가 — 기존 노쇼 제재(recruitBanUntil)와 동일 필드를 재사용하되
//       recruitBanReason으로 사유를 구분(노쇼 안내 문구가 신고 제재에 잘못 노출되는 것 방지)
// 75차: 신고관리 탭 상단 탭바에 이미 "🚨 신고관리"가 있는데 탭 내용 맨 위에도 같은
//       문구를 또 보여주고 있어 중복 — 제거하고 미처리 건수 배지만 남김
// 76차: RoleModal/PenaltyModal의 커스텀 제목 텍스트 → 공통 AppHeader(isModal)로 교체
// 78차: 처리/기각/직접부여/제재해제/모달 취소·저장 버튼을 공용 Secondary/GradientButton으로 교체

import React, { useState, useEffect, useCallback } from 'react';
import { showAlert } from '../../components/common/AppAlert';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet,
  TextInput, RefreshControl, Modal,
} from 'react-native';
import {
  collection, query, where, getDocs, doc, updateDoc,
  setDoc, orderBy, increment, addDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { applyReportBan, liftRecruitBan } from '../../services/recruitService';
import { deleteReview } from '../../services/reviewService';
import AppHeader, { RoleBadge } from '../../components/common/AppHeader';
import GradientButton from '../../components/common/GradientButton';
import { SecondaryButton } from '../../components/common/Buttons';
import LoadingOverlay from '../../components/common/LoadingOverlay';
import { PeopleIcon, IconLabel, CancelIcon, CheckmarkIcon, NoEntryIcon, ReportFlagIcon } from '../../components/common/Icons';
import { COLORS, RADIUS, SHADOW, FONTS } from '../../constants/theme';

const C = {
  bg:      COLORS.bg,
  surface: COLORS.surface,
  card:    COLORS.surface2,
  border:  COLORS.border,
  gold:    COLORS.gold,
  goldBg:  COLORS.goldBg,
  goldBdr: COLORS.goldBorder,
  onGold:  COLORS.onGold,
  text:    COLORS.text,
  textSec: COLORS.textSec,
  muted:   COLORS.textMuted,
  success: COLORS.green,
  danger:  COLORS.red,
  warning: COLORS.warning,
  dangerSoft:       COLORS.dangerSoft,
  dangerSoftBg:     COLORS.dangerSoftBg,
  dangerSoftBorder: COLORS.dangerSoftBorder,
};

const TABS = [
  { key:'users',   label:'유저관리', Icon:PeopleIcon },
  { key:'reports', label:'신고관리', Icon:ReportFlagIcon },
];

export default function SuperAdminUserReportScreen() {
  const [tab,        setTab]        = useState('users');
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefresh]    = useState(false);
  const [users,   setUsers]   = useState([]);
  const [stores,  setStores]  = useState([]);
  const [reports, setReports] = useState([]);

  const loadAll = useCallback(async () => {
    try {
      const [userSnap, storeSnap, reportSnap] = await Promise.all([
        getDocs(collection(db,'users')),
        getDocs(collection(db,'stores')),
        getDocs(query(collection(db,'reports'), orderBy('createdAt','desc'))),
      ]);
      setUsers(userSnap.docs.map(d=>({id:d.id,...d.data()})));
      setStores(storeSnap.docs.map(d=>({id:d.id,...d.data()})));
      setReports(reportSnap.docs.map(d=>({id:d.id,...d.data()})));
    } catch(e){ /* 무음 처리 */ }
    finally { setLoading(false); }
  }, []);

  useEffect(()=>{ loadAll(); },[loadAll]);
  const onRefresh = async ()=>{ setRefresh(true); await loadAll(); setRefresh(false); };

  const pendingReportCount = reports.filter(r=>(r.status||'pending')==='pending').length;

  // 73차: 로딩 중에도 AppHeader 유지 (탭 전환마다 헤더가 잠깐 사라지던 문제 수정)
  if (loading) return (
    <View style={s.container}>
      <AppHeader rightElement={<RoleBadge role="super" />} />
      <LoadingOverlay />
    </View>
  );

  return (
    <View style={s.container}>
      {/* 75차: 탭 이름 배지 제거(하단 탭바와 중복) — 미처리 신고 건수 배지만 유지 */}
      {/* 78차 37차: 총관리자 화면 전체 배지 통일 — RoleBadge 추가, 미처리 신고 건수는 옆에 그대로 유지 */}
      <AppHeader rightElement={
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <RoleBadge role="super" />
          {pendingReportCount > 0 && (
            <View style={{backgroundColor:C.danger,borderRadius:10,paddingHorizontal:8,paddingVertical:2}}>
              <Text style={{color:'#fff',fontSize:11,fontWeight:'800'}}>{pendingReportCount}</Text>
            </View>
          )}
        </View>
      }/>

      {/* 탭 바 */}
      <View style={s.tabRow}>
        {TABS.map(t=>(
          <TouchableOpacity key={t.key}
            style={[s.tabBtn, tab===t.key&&s.tabBtnOn]}
            onPress={()=>setTab(t.key)}>
            {t.Icon
              ? <IconLabel Icon={t.Icon} size={13} color={tab===t.key ? undefined : C.muted} textStyle={[s.tabTxt, tab===t.key&&s.tabTxtOn]}>{t.label}</IconLabel>
              : <Text style={[s.tabTxt, tab===t.key&&s.tabTxtOn]}>{t.label}</Text>}
            {t.key==='reports' && pendingReportCount > 0 && (
              <View style={s.tabBadge}>
                <Text style={s.tabBadgeTxt}>{pendingReportCount}</Text>
              </View>
            )}
          </TouchableOpacity>
        ))}
      </View>

      <ScrollView style={s.body}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={C.gold}/>}>
        {tab==='users'   && <AdminsTab users={users} stores={stores} onUpdate={loadAll}/>}
        {tab==='reports' && <ReportsTab reports={reports} users={users} onUpdate={loadAll}/>}
        <View style={{height:60}}/>
      </ScrollView>
    </View>
  );
}

// ── 유저 관리 탭 ──────────────────────────────────────────────
function AdminsTab({ users, stores, onUpdate }) {
  const [search,      setSearch]      = useState('');
  const [roleFilter,  setRoleFilter]  = useState('all');
  const [selUser,     setSelUser]     = useState(null);
  const [modal,       setModal]       = useState(false);
  const [directEmail, setDirectEmail] = useState('');

  const handleDirectGrant = async () => {
    const email = directEmail.trim().toLowerCase();
    if (!email) return showAlert('알림','이메일을 입력해주세요.');
    const existing = users.find(u=>(u.email||'').toLowerCase()===email);
    if (existing) { setSelUser(existing); setModal(true); return; }
    try {
      const q = query(collection(db,'users'), where('email','==',email));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const d = snap.docs[0];
        setSelUser({id:d.id,...d.data()});
        setModal(true);
      } else {
        showAlert('미가입 또는 미확인 계정',`${email} 계정이 목록에 없습니다.`,[
          {text:'취소',style:'cancel'},
          {text:'임시 부여',onPress:()=>{
            setSelUser({id:email.replace(/[@.]/g,'_'),email,nickname:'(미확인)',adminRole:null,_isTemp:true});
            setModal(true);
          }},
        ]);
      }
    } catch(e){ showAlert('오류',e.message); }
  };

  const ROLE_FILTERS = [
    {key:'all',label:'전체'},{key:'super',label:'총관리자'},
    {key:'store',label:'매장관리자'},{key:'user',label:'일반'},
  ];
  const roleOrder = r=>r==='super'?0:r==='store'?1:2;
  const filtered = users
    .filter(u=>{
      if(roleFilter==='super'&&u.adminRole!=='super') return false;
      if(roleFilter==='store'&&u.adminRole!=='store') return false;
      if(roleFilter==='user'&&(u.adminRole==='super'||u.adminRole==='store')) return false;
      if(!search) return true;
      const q=search.toLowerCase();
      return (u.email||'').toLowerCase().includes(q)||(u.nickname||'').toLowerCase().includes(q);
    })
    .sort((a,b)=>roleOrder(a.adminRole)-roleOrder(b.adminRole));

  const roleColor = r=>r==='super'?C.danger:r==='store'?C.warning:C.muted;
  const roleLabel = r=>r==='super'?'총관리자':r==='store'?'매장관리자':'일반';

  // 78차 71차: 이메일 가입 기존 회원을 매장관리자로 전환할 때도 등급(owner/staff)을
  // 지정할 수 있도록 — storeAdminTier는 로그인 방식(이메일 vs ID)과 무관한 필드라
  // 이 전환 경로에도 그대로 적용 가능. role이 'store'가 아니면 필드 자체를 지움
  // (일반회원/총관리자로 전환 시 이전 등급 값이 남아 혼란을 주지 않도록).
  const handleSave = async (uid, role, managedStores, storeAdminTier) => {
    try {
      if (selUser?._isTemp) {
        const pendingKey = selUser.email.replace(/[@.]/g,'_');
        await setDoc(doc(db,'pendingAdmins',pendingKey),{
          email:selUser.email, adminRole:role||null,
          isAdmin:!!role,
          managedStores:role==='store'?managedStores:[],
          storeAdminTier:role==='store'?storeAdminTier:null,
          createdAt:new Date().toISOString(),
        });
        showAlert('완료','권한이 예약되었습니다.');
      } else {
        await setDoc(doc(db,'users',uid),{
          adminRole:role||null, isAdmin:!!role,
          managedStores:role==='store'?managedStores:[],
          storeAdminTier:role==='store'?storeAdminTier:null,
        },{merge:true});
        showAlert('완료','권한이 변경되었습니다.');
      }
      setModal(false); setSelUser(null); setDirectEmail(''); onUpdate();
    } catch(e){ showAlert('오류','변경 실패: '+e.message); }
  };

  // reportStats 표시 헬퍼
  const renderReportStats = (u) => {
    const received  = u.reportStats?.receivedCount  ?? 0;
    const processed = u.reportStats?.processedCount ?? 0;
    if (received === 0) return null;
    return (
      <View style={s.reportStatsBadge}>
        <IconLabel Icon={ReportFlagIcon} size={11} color={C.dangerSoft} textStyle={s.reportStatsTxt}>
          {received}회 신고{processed > 0 ? ` · ${processed}회 처리` : ''}
        </IconLabel>
      </View>
    );
  };

  // 75차: 모집글 작성 제재(노쇼/신고) 상태 표시 + 수동 해제
  const isBanActive = (u) => u.recruitBanUntil && new Date(u.recruitBanUntil) > new Date();
  const handleLiftBan = (u) => {
    showAlert('제재 해제', `"${u.nickname||u.email}" 계정의 모집글 작성 제한을 해제하시겠습니까?`, [
      {text:'취소',style:'cancel'},
      {text:'해제',onPress: async ()=>{
        try { await liftRecruitBan(u.id); onUpdate(); showAlert('완료','제재가 해제되었습니다.'); }
        catch(e){ showAlert('오류',e.message); }
      }},
    ]);
  };
  const renderBanStatus = (u) => {
    if (!isBanActive(u)) return null;
    const isPermanent = u.recruitBanUntil === '9999-12-31T00:00:00.000Z';
    const reasonLabel = u.recruitBanReason === 'report' ? '신고 처리' : '노쇼';
    const label = isPermanent
      ? `모집글 작성 영구 제한 (${reasonLabel})`
      : `모집글 작성 제한 중 · ${reasonLabel} (~${new Date(u.recruitBanUntil).toLocaleDateString('ko-KR')})`;
    return (
      <View style={{ flexDirection:'row', alignItems:'center', gap:6, marginTop:5 }}>
        <View style={s.reportStatsBadge}>
          <IconLabel Icon={NoEntryIcon} size={s.reportStatsTxt.fontSize || 11} color={s.reportStatsTxt.color} textStyle={s.reportStatsTxt}>{label}</IconLabel>
        </View>
        <SecondaryButton label="해제" onPress={()=>handleLiftBan(u)} style={s.smBtn} textStyle={s.smBtnTxt} />
      </View>
    );
  };

  return (
    <View>
      <View style={[s.card,{marginBottom:16}]}>
        <Text style={s.sectionTitle}>📧 이메일로 직접 권한 부여</Text>
        <TextInput style={[s.searchInput,{marginBottom:8}]} value={directEmail} onChangeText={setDirectEmail}
          placeholder="권한 부여할 이메일 입력" placeholderTextColor={C.muted}
          keyboardType="email-address" autoCapitalize="none"/>
        <GradientButton label="🔍 검색 후 권한 설정" onPress={handleDirectGrant} style={s.primaryBtn} textStyle={s.primaryBtnTxt} />
      </View>

      <TextInput style={s.searchInput} value={search} onChangeText={setSearch}
        placeholder="이메일 또는 닉네임 검색" placeholderTextColor={C.muted}/>

      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:14}}>
        <View style={{flexDirection:'row',gap:8}}>
          {ROLE_FILTERS.map(f=>(
            <TouchableOpacity key={f.key}
              style={[s.chip,roleFilter===f.key&&s.chipOn]}
              onPress={()=>setRoleFilter(f.key)}>
              <Text style={[s.chipTxt,roleFilter===f.key&&s.chipTxtOn]}>{f.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>

      <Text style={s.sectionTitle}>회원 목록 ({filtered.length}명)</Text>
      {filtered.map(u=>(
        <TouchableOpacity key={u.id} style={s.card} onPress={()=>{setSelUser(u);setModal(true);}}>
          <View style={{flexDirection:'row',alignItems:'center',justifyContent:'space-between'}}>
            <View style={{flex:1}}>
              <View style={{flexDirection:'row',alignItems:'center',gap:6}}>
                <Text style={{color:C.text,fontWeight:'700',fontSize:14}}>
                  {u.nickname||'(닉네임 없음)'}
                </Text>
                {/* 74차: 영업용 데모 매장관리자 테스트 계정 배지 */}
                {u.isDemoAccount && (
                  <View style={{borderRadius:6,paddingHorizontal:6,paddingVertical:1,borderWidth:1,borderColor:C.warning+'60',backgroundColor:C.warning+'15'}}>
                    <Text style={{color:C.warning,fontSize:10,fontWeight:'800'}}>🧪 데모 계정</Text>
                  </View>
                )}
              </View>
              <Text style={s.muted}>{u.email||u.id}</Text>
              {/* 신고 통계 배지 */}
              {renderReportStats(u)}
              {/* 75차: 모집글 작성 제재 상태 + 해제 버튼 */}
              {renderBanStatus(u)}
            </View>
            <View style={{borderRadius:6,paddingHorizontal:8,paddingVertical:3,borderWidth:1,
              borderColor:roleColor(u.adminRole)+'60',backgroundColor:roleColor(u.adminRole)+'15'}}>
              <Text style={{color:roleColor(u.adminRole),fontSize:11,fontWeight:'700'}}>
                {roleLabel(u.adminRole)}
              </Text>
            </View>
          </View>
        </TouchableOpacity>
      ))}

      {/* 권한 변경 모달 */}
      <RoleModal
        visible={modal}
        user={selUser}
        stores={stores}
        onSave={handleSave}
        onClose={()=>{setModal(false);setSelUser(null);}}
      />
    </View>
  );
}

function RoleModal({ visible, user: selUser, stores, onSave, onClose }) {
  const [role,          setRole]          = useState(selUser?.adminRole||'');
  const [managedStores, setManagedStores] = useState(selUser?.managedStores||[]);
  // 78차 71차: 필드가 없는 기존 계정은 owner로 간주(하위호환)
  const [storeAdminTier, setStoreAdminTier] = useState(selUser?.storeAdminTier || 'owner');

  useEffect(()=>{
    if(selUser){
      setRole(selUser.adminRole||'');
      setManagedStores(selUser.managedStores||[]);
      setStoreAdminTier(selUser.storeAdminTier || 'owner');
    }
  },[selUser]);

  if (!selUser) return null;
  const ROLES = [{v:'',l:'일반 사용자'},{v:'store',l:'매장관리자'},{v:'super',l:'총관리자'}];

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={s.modalOverlay}>
        <View style={[s.modalBox,{padding:0,overflow:'hidden'}]}>
          <AppHeader isModal
            centerElement={<Text style={s.modalHeaderTitle}>권한 변경</Text>}
            rightElement={
              <TouchableOpacity onPress={onClose}>
                <IconLabel Icon={CancelIcon} size={13} color={C.muted} textStyle={{color:C.muted,fontSize:14}}>닫기</IconLabel>
              </TouchableOpacity>
            }
          />
          <View style={{padding:24}}>
          <Text style={[s.muted,{marginBottom:12}]}>{selUser.nickname||selUser.email}</Text>
          {ROLES.map(r=>(
            <TouchableOpacity key={r.v}
              style={[s.radioRow,role===r.v&&s.radioRowOn]}
              onPress={()=>setRole(r.v)}>
              <View style={[s.radio,role===r.v&&s.radioOn]}/>
              <Text style={s.radioLabel}>{r.l}</Text>
            </TouchableOpacity>
          ))}
          {role==='store' && (
            <>
              <Text style={[s.muted,{marginTop:12,marginBottom:8}]}>담당 매장 선택</Text>
              {stores.map(st=>{
                const on=managedStores.includes(st.id);
                return (
                  <TouchableOpacity key={st.id}
                    style={[s.checkRow]}
                    onPress={()=>setManagedStores(p=>on?p.filter(x=>x!==st.id):[...p,st.id])}>
                    <View style={[s.checkbox,on&&s.checkboxOn]}>
                      {on&&<CheckmarkIcon size={12} color={C.onGold} />}
                    </View>
                    <Text style={{color:C.text,fontSize:13}}>{st.ownerName}</Text>
                  </TouchableOpacity>
                );
              })}
              {/* 78차 71차: 등급 — 매출·통계가 보이는 "매장관리" 탭을 제외할지 여부 */}
              <Text style={[s.muted,{marginTop:16,marginBottom:8}]}>등급</Text>
              {[{v:'owner',l:'사장님 (전체 권한)'},{v:'staff',l:'직원 (매장관리 탭 제외)'}].map(t=>(
                <TouchableOpacity key={t.v}
                  style={[s.radioRow,storeAdminTier===t.v&&s.radioRowOn]}
                  onPress={()=>setStoreAdminTier(t.v)}>
                  <View style={[s.radio,storeAdminTier===t.v&&s.radioOn]}/>
                  <Text style={s.radioLabel}>{t.l}</Text>
                </TouchableOpacity>
              ))}
            </>
          )}
          <View style={{flexDirection:'row',gap:10,marginTop:20}}>
            <SecondaryButton label="취소" onPress={onClose} style={{flex:1}} textStyle={s.outlineBtnTxt} />
            <GradientButton label="저장" onPress={()=>onSave(selUser.id,role,managedStores,storeAdminTier)} style={{flex:2}} textStyle={s.primaryBtnTxt} />
          </View>
          </View>
        </View>
      </View>
    </Modal>
  );
}

// ── 신고 관리 탭 ──────────────────────────────────────────────
// 28차: 신고 처리(resolved) 시 피신고자 reportStats.processedCount +1
// 32차: 신고 처리/기각 후 피신고자 인앱 알림 발송
function ReportsTab({ reports, users, onUpdate }) {
  const [filter,        setFilter]        = useState('pending');
  const [processing,    setProcessing]    = useState(null);
  const [penaltyTarget, setPenaltyTarget] = useState(null); // 75차: 처리 시 제한 기간 선택 대상 신고
  const [historyFor,    setHistoryFor]    = useState(null); // 78차: "누구에게 신고당했는지" 이력 펼침 대상(targetUid)

  // 75차: 신고 자동처리 대신 "놓치지 않게" — 대기중 탭은 오래된(=놓치기 쉬운) 신고가
  // 위로 오도록 정렬 순서를 뒤집음(기본은 최신순이라 오래 방치된 신고가 아래로 밀림).
  const toMs = (v) => v?.toDate ? v.toDate().getTime() : (v ? new Date(v).getTime() : 0);
  const filtered = reports
    .filter(r => filter==='all' ? true : (r.status||'pending')===filter)
    .sort((a,b) => filter==='pending' ? toMs(a.createdAt)-toMs(b.createdAt) : 0);
  const OVERDUE_DAYS = 3;
  const isOverdue = (r) => (r.status||'pending')==='pending'
    && toMs(r.createdAt) > 0
    && (Date.now() - toMs(r.createdAt)) > OVERDUE_DAYS * 86400000;

  // 75차: action==='resolved'일 때 banDays(1/3/7)를 받아 모집글 작성 제한을 함께 적용
  const handleResolve = async (reportId, action, banDays = null) => {
    setProcessing(reportId);
    try {
      await updateDoc(doc(db,'reports',reportId),{
        status:action,
        resolvedAt:new Date().toISOString(),
        penaltyApplied: action === 'resolved',
        ...(action === 'resolved' && banDays ? { penaltyDays: banDays } : {}),
      });

      const report = reports.find(r=>r.id===reportId);

      if (action === 'resolved' && report?.targetUid) {
        // 신고 처리(resolved) 시 피신고자 processedCount +1
        try {
          await updateDoc(doc(db,'users',report.targetUid),{
            'reportStats.receivedCount':  increment(0), // 이미 신고 시점에 +1됨 (submitReportSafe에서)
            'reportStats.processedCount': increment(1),
          });
        } catch(e) { /* 무음 처리 */ }

        // 기간제 모집글 작성 제한 적용 (알림도 이 함수 안에서 발송)
        if (banDays) {
          try { await applyReportBan(report.targetUid, banDays); } catch(e) { /* 무음 처리 */ }
        }
        // 80차 후속: [기능 추가 — fork로 발견된 사각지대] 리뷰 신고(report.reviewId)가
        // "처리완료"돼도 지금까지 작성자 계정 제재만 될 뿐 신고된 리뷰 자체는 그대로
        // 공개 상태로 남아있었음(신고→처리 흐름이 콘텐츠엔 아무 영향이 없었음) — 처리
        // 확정은 곧 그 리뷰가 실제로 부적절하다고 판단했다는 뜻이므로, 같은 흐름에서
        // deleteReview()(포인트 환수/테마 통계 반영까지 이미 전담하는 기존 함수)로
        // 소프트삭제까지 같이 처리.
        if (report?.reviewId) {
          try { await deleteReview(report.reviewId); } catch(e) { /* 무음 처리 — 이미 삭제됐거나 없는 리뷰 */ }
        }
      } else if (report?.targetUid) {
        // 32차: 기각 시 피신고자 인앱 알림 발송
        addDoc(collection(db, 'notifications'), {
          uid: report.targetUid,
          type: 'report_result',
          title: '✅ 신고 처리 결과 안내',
          body: `"${report.title || '모집글'}"에 대한 신고가 검토 후 기각되었습니다.`,
          recruitId: report.recruitId || null,
          read: false,
          createdAt: serverTimestamp(),
        }).catch(() => {});
      }

      onUpdate();
      showAlert('완료', action === 'resolved' ? '신고가 처리되었습니다.' : '신고가 기각되었습니다.');
    } catch(e){ showAlert('오류',e.message); }
    finally { setProcessing(null); setPenaltyTarget(null); }
  };

  const statusColor = s => s==='resolved'?C.success:s==='dismissed'?C.muted:C.warning;
  const statusLabel = s => s==='resolved'?'처리완료':s==='dismissed'?'기각':'대기중';
  const pendingCount = reports.filter(r=>(r.status||'pending')==='pending').length;

  return (
    <View>
      {/* 75차: 위 탭바에 이미 "🚨 신고관리"가 있어 여기서 같은 문구를 또 보여주던 것 제거,
          미처리 건수 배지만 유지 */}
      {pendingCount>0&&(
        <View style={{flexDirection:'row',justifyContent:'flex-end',marginBottom:12}}>
          <View style={{backgroundColor:C.danger,borderRadius:12,paddingHorizontal:10,paddingVertical:3}}>
            <Text style={{color:'#fff',fontSize:12,fontWeight:'800'}}>미처리 {pendingCount}건</Text>
          </View>
        </View>
      )}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:14}}>
        <View style={{flexDirection:'row',gap:8}}>
          {[{k:'pending',l:'대기중'},{k:'resolved',l:'처리완료'},{k:'dismissed',l:'기각'},{k:'all',l:'전체'}].map(f=>(
            <TouchableOpacity key={f.k}
              style={[s.chip,filter===f.k&&s.chipOn]}
              onPress={()=>setFilter(f.k)}>
              <Text style={[s.chipTxt,filter===f.k&&s.chipTxtOn]}>{f.l}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>

      {filtered.length===0?<EmptyState Icon={CheckmarkIcon} text="해당 신고가 없습니다."/>:
        filtered.map(r=>{
          // 피신고자 정보 조회
          const targetUser = users.find(u=>u.id===r.targetUid);
          const receivedCount  = targetUser?.reportStats?.receivedCount  ?? 0;
          const processedCount = targetUser?.reportStats?.processedCount ?? 0;

          return (
            <View key={r.id} style={[s.card,{marginBottom:10}]}>
              <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                <View style={{flex:1}}>
                  <Text style={{color:C.text,fontWeight:'700',fontSize:14}} numberOfLines={1}>{r.reason||'사유 없음'}</Text>
                  <Text style={[s.muted,{marginTop:2}]}>신고자: {r.reporterName||'-'}  →  대상: {r.targetName||'-'}</Text>
                  {/* 피신고자 누적 신고 횟수 — 78차: 몇 번 누구에게 신고당했는지 바로 펼쳐볼 수 있게 */}
                  {receivedCount > 0 && (
                    <View style={{flexDirection:'row',gap:6,marginTop:5}}>
                      <TouchableOpacity
                        style={s.reportStatsBadge}
                        onPress={()=>setHistoryFor(p=>p===r.targetUid?null:r.targetUid)}>
                        <IconLabel Icon={ReportFlagIcon} size={11} color={C.dangerSoft} textStyle={s.reportStatsTxt}>
                          누적 신고 {receivedCount}회
                          {processedCount > 0 ? ` · 처리 ${processedCount}회` : ''}
                          {' '}{historyFor===r.targetUid?'▲':'▼'}
                        </IconLabel>
                      </TouchableOpacity>
                    </View>
                  )}
                  {/* 78차: 이 유저를 신고한 전체 이력(신고자/사유/날짜/처리상태) — 이 카드 하나만
                      보면 "이번 신고"만 보이던 것을, 같은 targetUid의 다른 신고 건까지 모아서 보여줌 */}
                  {historyFor===r.targetUid && (
                    <View style={s.historyBox}>
                      {reports.filter(x=>x.targetUid===r.targetUid).sort((a,b)=>toMs(b.createdAt)-toMs(a.createdAt)).map(h=>(
                        <View key={h.id} style={s.historyRow}>
                          <View style={{flex:1}}>
                            <Text style={s.historyTxt} numberOfLines={1}>{h.reporterName||'-'} · {h.reason||'사유 없음'}</Text>
                            <Text style={[s.muted,{fontSize:10}]}>{h.createdAt?.toDate?h.createdAt.toDate().toLocaleDateString('ko-KR'):''}</Text>
                          </View>
                          <Text style={{color:statusColor(h.status),fontSize:10,fontWeight:'700'}}>{statusLabel(h.status)}</Text>
                        </View>
                      ))}
                    </View>
                  )}
                </View>
                <View style={{backgroundColor:statusColor(r.status)+'20',borderRadius:8,borderWidth:1,
                  borderColor:statusColor(r.status)+'60',paddingHorizontal:8,paddingVertical:3,marginLeft:8}}>
                  <Text style={{color:statusColor(r.status),fontSize:11,fontWeight:'700'}}>{statusLabel(r.status)}</Text>
                </View>
              </View>
              {r.title&&<Text style={[s.muted,{marginBottom:6}]} numberOfLines={1}>모집글: "{r.title}"</Text>}
              <View style={{flexDirection:'row',alignItems:'center',gap:8,marginBottom:10}}>
                <Text style={s.muted}>
                  {r.createdAt?.toDate?r.createdAt.toDate().toLocaleString('ko-KR'):r.createdAt||'-'}
                </Text>
                {/* 75차: 3일 넘게 미처리된 신고를 놓치지 않도록 표시(자동 처리는 하지 않음) */}
                {isOverdue(r) && (
                  <View style={{backgroundColor:C.danger+'20',borderRadius:6,borderWidth:1,borderColor:C.danger+'60',paddingHorizontal:6,paddingVertical:1}}>
                    <Text style={{color:C.danger,fontSize:10,fontWeight:'700'}}>⏰ {OVERDUE_DAYS}일 이상 대기중</Text>
                  </View>
                )}
              </View>
              {(r.status||'pending')==='pending'&&(
                <View style={{flexDirection:'row',gap:8}}>
                  <SecondaryButton
                    label={processing===r.id?'처리중...':'✅ 처리'}
                    disabled={processing===r.id}
                    onPress={()=>setPenaltyTarget(r)}
                    style={[s.smBtn,{flex:1,borderColor:C.success+'60'}]}
                    textStyle={[s.smBtnTxt,{color:C.success}]}
                  />
                  <SecondaryButton
                    label="기각"
                    disabled={processing===r.id}
                    onPress={()=>showAlert('기각','기각하시겠습니까?',[
                      {text:'취소',style:'cancel'},
                      {text:'기각',onPress:()=>handleResolve(r.id,'dismissed')},
                    ])}
                    style={[s.smBtn,{flex:1}]}
                    textStyle={s.smBtnTxt}
                  />
                </View>
              )}
            </View>
          );
        })
      }

      {/* 75차: 처리 시 모집글 작성 제한 기간 선택 모달 */}
      <PenaltyModal
        visible={!!penaltyTarget}
        report={penaltyTarget}
        processing={processing===penaltyTarget?.id}
        onConfirm={(days)=>handleResolve(penaltyTarget.id,'resolved',days)}
        onClose={()=>setPenaltyTarget(null)}
      />
    </View>
  );
}

// 75차: 신고 처리 확정 + 모집글 작성 제한 기간(1/3/7일) 선택 모달
function PenaltyModal({ visible, report, processing, onConfirm, onClose }) {
  const [days, setDays] = useState(3);
  useEffect(()=>{ if (visible) setDays(3); }, [visible, report?.id]);
  if (!report) return null;
  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={s.modalOverlay}>
        <View style={[s.modalBox,{padding:0,overflow:'hidden'}]}>
          <AppHeader isModal
            centerElement={<Text style={s.modalHeaderTitle}>신고 처리</Text>}
            rightElement={
              <TouchableOpacity onPress={onClose}>
                <IconLabel Icon={CancelIcon} size={13} color={C.muted} textStyle={{color:C.muted,fontSize:14}}>닫기</IconLabel>
              </TouchableOpacity>
            }
          />
          <View style={{padding:24}}>
          <Text style={[s.muted,{marginBottom:16}]}>
            대상: {report.targetName||'-'}{'\n'}처리 시 선택한 기간만큼 모집글 작성이 제한됩니다.
          </Text>
          <View style={{flexDirection:'row',gap:8,marginBottom:20}}>
            {[1,3,7].map(d=>(
              <TouchableOpacity key={d}
                style={[s.chip,{flex:1,alignItems:'center'},days===d&&s.chipOn]}
                onPress={()=>setDays(d)}>
                <Text style={[s.chipTxt,days===d&&s.chipTxtOn]}>{d}일</Text>
              </TouchableOpacity>
            ))}
          </View>
          <View style={{flexDirection:'row',gap:10}}>
            <SecondaryButton label="취소" onPress={onClose} style={{flex:1}} textStyle={s.outlineBtnTxt} />
            <GradientButton
              label={processing?'처리 중...':'처리 확정'}
              disabled={processing}
              onPress={()=>onConfirm(days)}
              style={{flex:2}}
              textStyle={s.primaryBtnTxt}
            />
          </View>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function EmptyState({emoji,Icon,text}){return(<View style={{alignItems:'center',paddingVertical:40}}>{Icon?<View style={{marginBottom:12}}><Icon size={40} color={C.muted}/></View>:<Text style={{fontSize:40,marginBottom:12}}>{emoji}</Text>}<Text style={{color:C.muted,fontSize:12}}>{text}</Text></View>);}

const s = StyleSheet.create({
  container:    {flex:1,backgroundColor:C.bg},
  roleBadge:    {backgroundColor:C.goldBg,borderRadius:20,paddingHorizontal:10,paddingVertical:4,borderWidth:1,borderColor:C.goldBdr},
  roleBadgeTxt: {color:C.gold,fontSize:11,fontWeight:'700'},
  tabRow:       {flexDirection:'row',backgroundColor:C.surface,borderBottomWidth:1,borderBottomColor:C.border,paddingHorizontal:16,paddingVertical:8,gap:10},
  tabBtn:       {flex:1,paddingVertical:8,borderRadius:RADIUS.sm,borderWidth:1,borderColor:C.border,backgroundColor:C.card,alignItems:'center',flexDirection:'row',justifyContent:'center',gap:6},
  tabBtnOn:     {backgroundColor:C.goldBg,borderColor:C.goldBdr},
  tabTxt:       {color:C.muted,fontSize:13,fontWeight:'600'},
  tabTxtOn:     {color:C.gold,fontWeight:'800'},
  tabBadge:     {backgroundColor:C.danger,borderRadius:8,paddingHorizontal:6,paddingVertical:1},
  tabBadgeTxt:  {color:'#fff',fontSize:10,fontWeight:'800'},
  body:         {flex:1,padding:16},
  sectionTitle: {fontSize:14,fontWeight:'700',color:C.gold,marginBottom:10,marginTop:6,letterSpacing:0.5,fontFamily:FONTS.display},
  modalHeaderTitle: {color:C.text,fontSize:15,fontWeight:'800'},
  muted:        {color:C.muted,fontSize:12},
  card:         {backgroundColor:C.card,borderRadius:RADIUS.md,borderWidth:1,borderColor:C.border,padding:16,marginBottom:12,...SHADOW.card},
  searchInput:  {backgroundColor:C.card,borderWidth:1,borderColor:C.border,borderRadius:RADIUS.sm,padding:12,color:C.text,marginBottom:14},
  chip:         {paddingHorizontal:12,paddingVertical:7,borderRadius:20,borderWidth:1,borderColor:C.border,backgroundColor:C.surface},
  chipOn:       {backgroundColor:C.goldBg,borderColor:C.goldBdr},
  chipTxt:      {color:C.muted,fontSize:12},
  chipTxtOn:    {color:C.gold,fontWeight:'600'},
  smBtn:        {paddingHorizontal:12,paddingVertical:6},
  smBtnTxt:     {fontSize:12},
  primaryBtn:   {marginTop:4},
  primaryBtnTxt:{fontSize:14},
  outlineBtnTxt:{fontSize:13},
  radioRow:     {flexDirection:'row',alignItems:'center',gap:12,padding:10,borderRadius:RADIUS.sm,borderWidth:1,borderColor:C.border,marginBottom:8},
  radioRowOn:   {borderColor:C.goldBdr,backgroundColor:C.goldBg},
  radio:        {width:18,height:18,borderRadius:9,borderWidth:2,borderColor:C.muted},
  radioOn:      {borderColor:C.gold,backgroundColor:C.gold},
  radioLabel:   {color:C.text,fontSize:14},
  checkRow:     {flexDirection:'row',alignItems:'center',gap:12,paddingVertical:8},
  checkbox:     {width:18,height:18,borderRadius:4,borderWidth:2,borderColor:C.muted,alignItems:'center',justifyContent:'center'},
  checkboxOn:   {backgroundColor:C.gold,borderColor:C.gold},
  modalOverlay: {flex:1,backgroundColor:'rgba(0,0,0,0.75)',justifyContent:'flex-end'},
  modalBox:     {backgroundColor:C.surface,borderTopLeftRadius:RADIUS.md,borderTopRightRadius:RADIUS.md,padding:24,paddingBottom:40},
  // 신고 통계 배지
  reportStatsBadge: {
    alignSelf:'flex-start', marginTop:5,
    backgroundColor:C.dangerSoftBg,
    borderRadius:6, borderWidth:1, borderColor:C.dangerSoftBorder,
    paddingHorizontal:8, paddingVertical:3,
  },
  reportStatsTxt: { color:C.dangerSoft, fontSize:11, fontWeight:'700' },
  // 78차: 유저별 신고 이력 펼침 박스
  historyBox: { marginTop:8, backgroundColor:C.bg, borderRadius:8, borderWidth:1, borderColor:C.border, padding:8, gap:6 },
  historyRow: { flexDirection:'row', alignItems:'center', gap:8 },
  historyTxt: { color:C.text, fontSize:12, fontWeight:'600' },
});